/**
 * JSON Schema definitions for job parameter validation using AJV
 * These schemas match the validation rules in lambda/call_ecs_over_https.py
 */

/**
 * Fit Job Schema
 * Validates parameters for Bayesian model fitting
 */
export const fitJobSchema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    title: 'Fit Job Parameters',
    description: 'Schema for fit job type validation',
    properties: {
        job_type: {
            type: 'string',
            enum: ['fit'],
            description: 'Job type identifier'
        },
        df_path: {
            type: 'string',
            description: 'Path to input CSV file',
            minLength: 1
        },
        future_df_path: {
            type: 'string',
            description: 'Path to future/forecast CSV file',
            minLength: 1
        },
        saturation_df_path: {
            type: 'string',
            description: 'Path to saturation curve CSV file',
            minLength: 1
        },
        kpi_col: {
            type: 'string',
            description: 'Column name for KPI metric',
            minLength: 1
        },
        date_col: {
            type: 'string',
            description: 'Column name for dates',
            minLength: 1
        },
        resource_cols: {
            type: 'array',
            items: {
                type: 'string'
            },
            description: 'List of resource/channel column names',
            minItems: 1
        },
        event_cols: {
            type: 'array',
            items: {
                type: 'string'
            },
            description: 'Optional list of event column names'
        },
        seasonality: {
            type: 'array',
            items: {
                type: 'integer'
            },
            description: 'Optional list of seasonality periods'
        },
        fs_orders: {
            type: 'array',
            items: {
                type: 'number'
            },
            description: 'Optional list of Fourier series orders'
        },
        fit_args: {
            type: 'object',
            description: 'MCMC sampling parameters',
            properties: {
                num_warmup: {
                    type: 'integer',
                    minimum: 10,
                    maximum: 1000,
                    description: 'Number of warmup iterations'
                },
                num_samples: {
                    type: 'integer',
                    minimum: 10,
                    maximum: 1000,
                    description: 'Number of posterior samples'
                },
                num_chains: {
                    type: 'integer',
                    minimum: 1,
                    maximum: 10,
                    description: 'Number of MCMC chains'
                }
            },
            required: ['num_warmup', 'num_samples', 'num_chains'],
            additionalProperties: false
        },
        run_tag: {
            type: 'string',
            description: 'Unique identifier for this run',
            minLength: 1,
            maxLength: 100
        },
        label: {
            type: 'string',
            description: 'Optional descriptive label for the job',
            maxLength: 255
        }
    },
    required: [
        'job_type',
        'df_path',
        'future_df_path',
        'saturation_df_path',
        'kpi_col',
        'date_col',
        'resource_cols',
        'run_tag',
        'fit_args'
    ],
    additionalProperties: false
};

/**
 * Attribution Job Schema
 * Validates parameters for attribution analysis
 */
export const attributeJobSchema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    title: 'Attribution Job Parameters',
    description: 'Schema for attribute job type validation',
    properties: {
        job_type: {
            type: 'string',
            enum: ['attribute'],
            description: 'Job type identifier'
        },
        model_id: {
            type: 'string',
            description: 'ID of the fitted model to analyze',
            minLength: 1,
            maxLength: 100
        },
        run_tag: {
            type: 'string',
            description: 'Unique identifier for this attribution run',
            minLength: 1,
            maxLength: 100
        }
    },
    required: ['job_type', 'model_id', 'run_tag'],
    additionalProperties: false
};

/**
 * Target Maximization Job Schema
 * Validates parameters for budget optimization
 */
export const targetMaxJobSchema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    title: 'Target Maximization Job Parameters',
    description: 'Schema for target_max job type validation',
    properties: {
        job_type: {
            type: 'string',
            enum: ['target_max'],
            description: 'Job type identifier'
        },
        model_id: {
            type: 'string',
            description: 'ID of the fitted model to optimize',
            minLength: 1,
            maxLength: 100
        },
        run_tag: {
            type: 'string',
            description: 'Unique identifier for this optimization run',
            minLength: 1,
            maxLength: 100
        },
        budget_start_date: {
            type: 'string',
            format: 'date',
            description: 'Start date for budget optimization period (YYYY-MM-DD)'
        },
        budget_end_date: {
            type: 'string',
            format: 'date',
            description: 'End date for budget optimization period (YYYY-MM-DD)'
        },
        variants: {
            type: 'array',
            description: 'Optional array of budget variant scenarios',
            items: {
                type: 'object'
            }
        }
    },
    required: [
        'job_type',
        'model_id',
        'run_tag',
        'budget_start_date',
        'budget_end_date'
    ],
    additionalProperties: false
};

/**
 * Basic validation schema for initial payload check
 * Ensures operation and job_type are present
 */
export const basicCheckSchema = {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    title: 'Basic Payload Check',
    description: 'Initial validation schema for all job submissions',
    properties: {
        operation: {
            type: 'string',
            enum: ['job_param'],
            description: 'Operation type'
        },
        payload: {
            type: 'object',
            properties: {
                job_type: {
                    type: 'string',
                    enum: ['fit', 'attribute', 'target_max'],
                    description: 'Job type identifier'
                }
            },
            required: ['job_type']
        }
    },
    required: ['operation', 'payload']
};

/**
 * Map of job types to their corresponding schemas
 */
export const jobSchemaMap = {
    fit: fitJobSchema,
    attribute: attributeJobSchema,
    target_max: targetMaxJobSchema
};

/**
 * Get the appropriate schema for a given job type
 * @param {string} jobType - The job type (fit, attribute, target_max)
 * @returns {object} The JSON Schema for the job type
 * @throws {Error} If job type is not recognized
 */
export const getSchemaForJobType = (jobType) => {
    if (!jobSchemaMap[jobType]) {
        throw new Error(`Unknown job type: ${jobType}`);
    }
    return jobSchemaMap[jobType];
};

/**
 * Get all available job types
 * @returns {string[]} Array of available job types
 */
export const getAvailableJobTypes = () => {
    return Object.keys(jobSchemaMap);
};

/**
 * Custom format validators for AJV
 * These can be added to an AJV instance for enhanced validation
 */
export const customFormats = {
    'date': {
        type: 'string',
        validate: (value) => {
            // Validate YYYY-MM-DD format
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(value)) {
                return false;
            }
            // Validate that it's a valid date
            const date = new Date(value);
            return date instanceof Date && !isNaN(date);
        }
    },
    'csv-path': {
        type: 'string',
        validate: (value) => {
            // Validate CSV file path format
            return /^[a-zA-Z0-9._\-/]+\.csv$/.test(value);
        }
    }
};

/**
 * AJV configuration options
 * These options are recommended for this validation use case
 */
export const ajvConfig = {
    allErrors: true,           // Collect all errors instead of stopping at first
    verbose: true,             // Include error details
    strict: false,             // Allow additional properties (more lenient)
    useDefaults: false,        // Don't apply default values automatically
    coerceTypes: false,        // Don't coerce types (strict type checking)
    removeAdditional: false,   // Keep additional properties
    formats: {
        date: true,              // Enable date format validation
        'date-time': true        // Enable date-time format validation
    }
};
